// Dialog de "marcar deal como perdido" (Story 11.4 / AC6 / AC10) — Radix Dialog.
// Exige motivo (lost_reason) com ao menos 5 caracteres antes de submeter.
// DESIGN_SYSTEM §componentes/Modal: padding 28, border-line, radius r4, shadow-lg.
// Espelha DiscardLeadDialog (ADAPT — mesmo padrão de motivo obrigatório).

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@control-center/ui';

import { Field, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

const MIN_REASON = 5;

interface LostDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealTitle: string;
  isPending?: boolean;
  onConfirm: (reason: string) => void;
}

export function LostDealDialog({
  open,
  onOpenChange,
  dealTitle,
  isPending,
  onConfirm,
}: LostDealDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  // Reseta o campo a cada abertura.
  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON;
  const error = touched && !isValid ? 'Descreva o motivo com ao menos 5 caracteres.' : undefined;

  const handleConfirm = (): void => {
    setTouched(true);
    if (!isValid) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          aria-describedby="lost-deal-desc"
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(460px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel shadow-lg',
            'p-7 focus:outline-none',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                Marcar deal como perdido
              </Dialog.Title>
              <p id="lost-deal-desc" className="mt-2 text-[13px] leading-[1.55] text-muted">
                Fechar <span className="font-medium text-ink-2">{dealTitle}</span> como perdido. O
                deal fica preservado com o histórico — só não pode mais ser editado.
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-5">
            <Field label="Motivo da perda" required error={error}>
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  rows={3}
                  autoFocus
                  placeholder="Ex: escolheu o concorrente, sem orçamento, projeto adiado…"
                  hasError={!!error}
                  value={reason}
                  onBlur={() => setTouched(true)}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <Dialog.Close asChild>
              <PillButton disabled={isPending}>Voltar</PillButton>
            </Dialog.Close>
            <PillButton danger onClick={handleConfirm} disabled={isPending || !isValid}>
              {isPending ? 'Marcando…' : 'Marcar como perdido'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
